import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ShieldCheck, Video, Presentation, Check, X } from "lucide-react";
import { IMAGES } from "@/lib/content";
import { KineticLines } from "@/components/landing/Reveal";
import { EligibilityButton } from "@/components/landing/EligibilityButton";

const chaos = [
  "Watching videos alone",
  "Random Telegram tips",
  "Trading on emotion",
  "No one checks your charts",
];

const classroom = [
  "Mentor sitting beside you",
  "Live trading floor sessions",
  "One-on-one desk reviews",
  "Recordings + online support",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} className="relative overflow-hidden diag-grid" data-testid="hero-section">
      {/* radial emerald glow behind headline */}
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/4 top-10 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-em1/25 blur-[130px]"
      />
      <div className="pointer-events-none absolute -right-40 top-40 h-96 w-96 rounded-full bg-em2/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-12 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-24 lg:pt-20">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-surface px-4 py-1.5 text-xs font-semibold text-gold"
              data-testid="hero-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-em2 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-em2" />
              </span>
              Ahmedabad's Trusted Trading Academy — Offline & Online Support
            </span>
          </motion.div>

          <KineticLines
            className="mt-6 font-display text-[2.6rem] font-bold uppercase leading-[0.95] tracking-tight text-offwhite sm:text-6xl lg:text-[4.1rem]"
            lines={[
              "Stop Watching",
              "Videos Alone.",
              "Learn Live Trading With a",
              "Mentor Beside You.",
            ]}
            accentIndex={3}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-mutedgreen sm:text-lg"
          >
            With over 10 years of experience and 1,000+ students trained, BullsEdge
            Academy teaches you the market the old-school way — face to face, in a
            real classroom, with someone guiding you every step. Can't make it in
            person? We've got you covered with online support too.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            className="mt-8 flex flex-col items-start gap-4"
          >
            <EligibilityButton size="lg" testId="hero-inquire-btn" />
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-offwhite">
                <span className="flex text-gold">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={15} fill="currentColor" />
                  ))}
                </span>
                Trusted by 1,000+ Students in Ahmedabad
              </div>
              <div className="flex items-center gap-2 text-sm text-mutedgreen">
                <ShieldCheck size={15} className="text-em2" />
                Offline Classes · One-on-One Mentorship · Live Trading with Mentors
              </div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border border-surfaceline glow-emerald">
              <motion.img
                style={{ y: imgY, objectPosition: "50% 55%" }}
                src={IMAGES.heroClassroom}
                alt="BullsEdge Ahmedabad classroom with mentor"
                className="h-80 w-full object-cover sm:h-96 lg:h-[28rem]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-em2/40 bg-ink/70 px-3 py-1.5 text-xs font-semibold text-em2 backdrop-blur">
                <Presentation size={14} /> Live Classroom Floor · Prahladanagar
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-red-400">
                  <Video size={13} /> Alone
                </p>
                <ul className="space-y-1.5">
                  {chaos.map((c) => (
                    <li key={c} className="flex items-start gap-1.5 text-xs text-mutedgreen">
                      <X size={13} className="mt-0.5 shrink-0 text-red-400/80" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-em2/30 bg-em2/[0.07] p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-wider text-em2">
                  <Presentation size={13} /> BullsEdge
                </p>
                <ul className="space-y-1.5">
                  {classroom.map((c) => (
                    <li key={c} className="flex items-start gap-1.5 text-xs text-offwhite">
                      <Check size={13} className="mt-0.5 shrink-0 text-em2" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="gold-divider" />
      </div>
    </section>
  );
};
